import { useCallback, useEffect, useState } from "react";
import intl from "react-intl-universal";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import { useBaasPlatformCreateAccess } from "../../../../../Hooks/useBaasPlatformCreateAccess";
import {
  fetchAccountInboundPixOriginLock,
  putAccountInboundPixOriginLock,
} from "../../../../../Services/baasAccountInboundPixOriginLockApi";
import { formatBaasTaxDocument } from "../../../../../Services/baasInstanceRootDocument";

const BORDER_CARD =
  "rounded-xl border border-gray-200 bg-white dark:border-brand-dark-border dark:bg-brand-dark-surface";

const toggleTrackClass = (on) =>
  `relative inline-flex h-6 w-11 shrink-0 rounded-full border transition ${
    on
      ? "border-[#e7a73f] bg-[#e7a73f]"
      : "border-gray-300 bg-gray-200 dark:border-brand-dark-border dark:bg-brand-dark-elevated"
  }`;

const toggleThumbClass = (on) =>
  `pointer-events-none absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${
    on ? "left-[1.35rem]" : "left-0.5"
  }`;

const inputClass =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-brand-dark outline-none focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

const inputReadOnlyClass =
  `${inputClass} cursor-default bg-gray-50 text-gray-700 dark:bg-brand-dark-surface dark:text-gray-300`;

const digitsOnly = (value) => String(value || "").replace(/\D/g, "");

const ToggleRow = ({ label, hint, checked, onChange, disabled }) => (
  <div className="flex w-full items-center justify-between gap-3 rounded-xl border border-gray-200 px-3 py-2.5 dark:border-brand-dark-border">
    <div className="min-w-0">
      <p className="text-sm font-medium text-brand-dark dark:text-gray-100">{label}</p>
      {hint ? <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p> : null}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={`${toggleTrackClass(checked)} ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
    >
      <span className={toggleThumbClass(checked)} />
    </button>
  </div>
);

/**
 * Valut-admin panel: per-account "Travar origem PIX".
 * When enabled, origin document is filled from this Conta Digital's owner
 * (Account / entity profile / Person NationalRegistration) — never instance Root.
 */
export const BaasAccountInboundPixOriginLockPanel = ({
  accountId,
  account = null,
  inModal = false,
}) => {
  const { selectedInstance } = useBaasInstance();
  // Valut-only (matrix / Visualizar como / INFRA). Partners never see this panel.
  const { canUseViewAs, viewAsActive } = useBaasPlatformCreateAccess();
  const canSeeLock = Boolean(canUseViewAs);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resolvingAccountDoc, setResolvingAccountDoc] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [instanceLockEnabled, setInstanceLockEnabled] = useState(false);
  const [document, setDocument] = useState("");
  const [suggestedDocument, setSuggestedDocument] = useState("");
  const [loadedEnabled, setLoadedEnabled] = useState(false);
  const [loadedDocument, setLoadedDocument] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const inherited = Boolean(instanceLockEnabled);
  const canEdit = canSeeLock && !inherited;

  const accountPropDigits = digitsOnly(
    account?.nationalRegistration
    || account?.national_registration
    || account?.document
    || account?.raw?.national_registration
    || account?.raw?.nationalRegistration
    || "",
  );

  const load = useCallback(async () => {
    if (!canSeeLock || !selectedInstance || !accountId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const next = await fetchAccountInboundPixOriginLock(selectedInstance, accountId);
      const nextInstanceLock = Boolean(next?.instanceLockEnabled);
      const accountLockOn = Boolean(next?.enabled);
      const nextEnabled = nextInstanceLock || accountLockOn;
      // API: stored lock doc when enabled, or suggested Conta Digital owner when off.
      // Never fall back to instance Root / Valut matrix here.
      const apiDigits = digitsOnly(next?.lockedOriginDocument);
      const suggested = apiDigits || accountPropDigits;
      setInstanceLockEnabled(nextInstanceLock);
      setEnabled(nextEnabled);
      setSuggestedDocument(suggested ? formatBaasTaxDocument(suggested) : "");
      setDocument(nextEnabled && suggested ? formatBaasTaxDocument(suggested) : "");
      setLoadedEnabled(accountLockOn && !nextInstanceLock);
      setLoadedDocument(
        accountLockOn && !nextInstanceLock && apiDigits
          ? formatBaasTaxDocument(apiDigits)
          : "",
      );
    } catch (err) {
      setError(err?.detail || err?.message || intl.get("baas.accounts.inboundPixOriginLock.loadError"));
    } finally {
      setLoading(false);
    }
  }, [accountId, accountPropDigits, canSeeLock, selectedInstance]);

  useEffect(() => {
    load();
  }, [load]);

  const applyAccountDocument = useCallback(async () => {
    setResolvingAccountDoc(true);
    setError("");
    try {
      let digits = accountPropDigits || digitsOnly(suggestedDocument);
      if (!digits && selectedInstance && accountId) {
        const next = await fetchAccountInboundPixOriginLock(selectedInstance, accountId).catch(() => null);
        digits = digitsOnly(next?.lockedOriginDocument);
      }
      const formatted = digits ? formatBaasTaxDocument(digits) : "";
      setDocument(formatted);
      if (formatted) setSuggestedDocument(formatted);
      if (!formatted) {
        setError(intl.get("baas.accounts.inboundPixOriginLock.rootDocumentMissing"));
      }
      return formatted;
    } catch (err) {
      setDocument("");
      setError(
        err?.detail
          || err?.message
          || intl.get("baas.accounts.inboundPixOriginLock.rootDocumentMissing"),
      );
      return "";
    } finally {
      setResolvingAccountDoc(false);
    }
  }, [accountId, accountPropDigits, selectedInstance, suggestedDocument]);

  const handleEnabledChange = async (nextEnabled) => {
    if (!canEdit || saving || resolvingAccountDoc || inherited) return;
    setSuccess("");
    if (!nextEnabled) {
      setEnabled(false);
      setDocument("");
      setError("");
      return;
    }
    setEnabled(true);
    await applyAccountDocument();
  };

  const dirty =
    enabled !== loadedEnabled
    || digitsOnly(document) !== digitsOnly(loadedDocument);

  const handleSave = async () => {
    if (!canEdit || !dirty || saving || resolvingAccountDoc || inherited) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const digits = digitsOnly(document);
      const next = await putAccountInboundPixOriginLock(selectedInstance, accountId, {
        enabled,
        // Omit empty document when enabling so API infers Conta Digital owner (not Root).
        lockedOriginDocument: enabled ? (digits || undefined) : "",
      });
      const nextEnabled = Boolean(next?.enabled);
      const nextDoc = next?.lockedOriginDocument || "";
      setEnabled(nextEnabled);
      setDocument(nextDoc ? formatBaasTaxDocument(nextDoc) : "");
      setSuggestedDocument(nextDoc ? formatBaasTaxDocument(nextDoc) : suggestedDocument);
      setLoadedEnabled(nextEnabled);
      setLoadedDocument(nextDoc ? formatBaasTaxDocument(nextDoc) : "");
      setSuccess(intl.get("baas.accounts.inboundPixOriginLock.saveSuccess"));
    } catch (err) {
      const isAdminDeny =
        err?.status === 403
        || String(err?.code || "").includes("valut_admin")
        || String(err?.code || "").includes("instance_origin_lock");
      setError(
        isAdminDeny
          ? intl.get("baas.accounts.inboundPixOriginLock.adminOnly")
          : err?.detail || err?.message || intl.get("baas.accounts.inboundPixOriginLock.saveError"),
      );
    } finally {
      setSaving(false);
    }
  };

  if (!canSeeLock || !accountId) return null;

  return (
    <section>
      {inModal ? null : (
        <p className="dashboard-section-label mb-3">
          {intl.get("baas.accounts.inboundPixOriginLock.sectionTitle")}
        </p>
      )}
      <div className={inModal ? "space-y-4" : `${BORDER_CARD} space-y-4 px-5 py-4 sm:px-6`}>
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.accounts.inboundPixOriginLock.loading")}
          </p>
        ) : (
          <>
            <ToggleRow
              label={intl.get("baas.accounts.inboundPixOriginLock.label")}
              hint={intl.get("baas.accounts.inboundPixOriginLock.hint")}
              checked={enabled}
              onChange={handleEnabledChange}
              disabled={!canEdit || saving || resolvingAccountDoc || inherited}
            />
            {enabled ? (
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  {intl.get("baas.accounts.inboundPixOriginLock.documentLabel")}
                </label>
                <input
                  className={inputReadOnlyClass}
                  value={resolvingAccountDoc
                    ? intl.get("baas.accounts.inboundPixOriginLock.rootDocumentLoading")
                    : document}
                  readOnly
                  disabled
                  placeholder={intl.get("baas.accounts.inboundPixOriginLock.documentPlaceholder")}
                />
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  {intl.get("baas.accounts.inboundPixOriginLock.documentHint")}
                </p>
              </div>
            ) : null}
            {inherited ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {intl.get("baas.accounts.inboundPixOriginLock.instanceInherited")}
              </p>
            ) : !canEdit ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {intl.get("baas.accounts.inboundPixOriginLock.adminOnly")}
              </p>
            ) : viewAsActive ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {intl.get("baas.accounts.inboundPixOriginLock.viewAsNote")}
              </p>
            ) : null}
            {error ? (
              <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                {error}
              </p>
            ) : null}
            {success ? (
              <p role="status" className="text-sm text-emerald-600 dark:text-emerald-400">
                {success}
              </p>
            ) : null}
            {canEdit && !inherited ? (
              <div className="flex justify-end">
                <button
                  type="button"
                  className="btn-primary w-auto !text-xs"
                  onClick={handleSave}
                  disabled={!dirty || saving || resolvingAccountDoc || (enabled && !digitsOnly(document))}
                >
                  {saving
                    ? intl.get("baas.accounts.inboundPixOriginLock.saving")
                    : intl.get("baas.accounts.inboundPixOriginLock.save")}
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
};

export default BaasAccountInboundPixOriginLockPanel;
